import type { Album, AlbumCatalogue, AlbumPage, AlbumSpread, AlbumTemplatePlace } from '../types';

// ADR-0052: the book as the server keeps it — a cover, then the pages two at a time.

export const pagesOf = (album: Pick<Album, 'spreads'>): AlbumPage[] => album.spreads.flatMap(s => s.pages);

export function spreadOfPage(album: Pick<Album, 'spreads'>, pageId: string | null): AlbumSpread | undefined {
  return pageId ? album.spreads.find(s => s.pages.some(p => p.id === pageId)) : undefined;
}

export function placeOf(catalogue: AlbumCatalogue, template: string): AlbumTemplatePlace {
  return catalogue.templates.find(tp => tp.id === template)?.place ?? 'page';
}

/** The canvas a page draws on, in mm: one page, two, or two and the spine for the cover's wrap. */
export function pageWidth(catalogue: AlbumCatalogue, page: Pick<AlbumPage, 'side'>, kind: AlbumSpread['kind']): number {
  if (page.side !== 'BOTH') return catalogue.page.width;
  return 2 * catalogue.page.width + (kind === 'COVER' ? catalogue.spine.width : 0);
}

/** The width of a spread laid open, in mm. */
export function spreadWidth(catalogue: AlbumCatalogue, kind: AlbumSpread['kind']): number {
  return 2 * catalogue.page.width + (kind === 'COVER' ? catalogue.spine.width : 0);
}

/** Can this page take this template? The server says the same (`spreads.ts`). */
export function templateFits(kind: AlbumSpread['kind'], place: AlbumTemplatePlace): boolean {
  if (place === 'page') return true;
  return (kind === 'INNER' && place === 'spread') || (kind === 'COVER' && place === 'cover');
}

export type SpreadName =
  | { kind: 'cover' }
  | { kind: 'pages'; from: number; to: number };

/** The printed page numbers a spread holds: the opening is page 1, on the right. */
export function spreadNames(spreads: AlbumSpread[]): Map<string, SpreadName> {
  const names = new Map<string, SpreadName>();
  let next = 1;
  for (const s of spreads) {
    if (s.kind === 'COVER') { names.set(s.id, { kind: 'cover' }); continue; }
    const count = s.pages.reduce((n, p) => n + (p.side === 'BOTH' ? 2 : 1), 0);
    names.set(s.id, { kind: 'pages', from: next, to: next + count - 1 });
    next += count;
  }
  return names;
}

/** The printed number of one page, or which side of the cover it is. */
export function pageName(spreads: AlbumSpread[], page: AlbumPage): { cover: 'back' | 'front' | 'wrap' } | { from: number; to: number } | null {
  const spread = spreads.find(s => s.pages.some(p => p.id === page.id));
  if (!spread) return null;
  if (spread.kind === 'COVER') return { cover: page.side === 'LEFT' ? 'back' : page.side === 'RIGHT' ? 'front' : 'wrap' };
  const name = spreadNames(spreads).get(spread.id);
  if (!name || name.kind !== 'pages') return null;
  if (page.side === 'BOTH' || name.from === name.to) return { from: name.from, to: name.to };
  const n = page.side === 'LEFT' ? name.from : name.to;
  return { from: n, to: n };
}

/** One page's worth of a spread: the spread drawn whole and cut at `x` mm, one page wide. */
export interface BookFace {
  spread: AlbumSpread;
  x: number;
}

/** What lies open while leafing through: a null side is the table (the book closed). */
export interface BookView {
  left: BookFace | null;
  right: BookFace | null;
}

/**
 * The album as a book to leaf through: the front cover alone, each spread open
 * (the opening's left is the inside of the cover), and the back cover alone.
 * Turning from view i to i+1 lifts i's right face; its back is i+1's left.
 * The wrap cover (one page across both) is cut like any other: its right half is the front.
 */
export function bookViews(catalogue: AlbumCatalogue, spreads: AlbumSpread[]): BookView[] {
  const W = catalogue.page.width;
  const cover = spreads.find(s => s.kind === 'COVER');
  const open = spreads.filter(s => s.kind !== 'COVER').map(s => ({ left: { spread: s, x: 0 }, right: { spread: s, x: W } }));
  if (!cover) return open;
  return [
    { left: null, right: { spread: cover, x: W + catalogue.spine.width } },
    ...open,
    { left: { spread: cover, x: 0 }, right: null },
  ];
}
